import {Injectable} from '@angular/core';
import {Course} from '../models/course.model';
import {HttpRequestsConfigService} from './http-requests-config.service';
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ArchiveService {
  private allArchivedCourses: Course[] = [];
  private filteredArchivedCourses: Course[] = [];
  private _archiveCoursesSubject = new BehaviorSubject<Course[]>([]);
  public archiveCourses$ = this._archiveCoursesSubject.asObservable();

  constructor(
    private readonly httpRequestConfig: HttpRequestsConfigService
  ) {
  }

  private isDataLoaded = false;
  private allCoursesFromServer: Course[] = [];

  // Load all archived courses (past courses) - only load from server once
  public loadAllArchivedCourses(): void {
    if (!this.isDataLoaded) {
      // Only load from server if not already loaded
      this.httpRequestConfig.getAll<Course>('/courses')
        .subscribe({
          next: (response: any) => {
            let coursesData = response;
            if (response && response.courses) {
              coursesData = response.courses;
            }

            // Store all courses from server
            this.allCoursesFromServer = [...coursesData];

            // Filter to get only archived courses (past courses)
            this.allArchivedCourses = this.allCoursesFromServer.filter((course: Course) => {
              if (course.end_date) {
                // Parse the end date and compare with current date at the start of today
                const endDate = new Date(course.end_date);
                const currentDate = new Date();

                // Set current date to start of day to ensure we're comparing properly
                currentDate.setHours(0, 0, 0, 0);

                // Only include courses where the end date is BEFORE today (not including today)
                // This means courses ending TODAY remain visible in the main program until tomorrow
                return endDate < currentDate;
              }
              return false; // Exclude courses without end date
            });

            // Set the initial filtered list to all archived courses
            this.filteredArchivedCourses = [...this.allArchivedCourses];
            this.isDataLoaded = true;
            this._archiveCoursesSubject.next(this.filteredArchivedCourses);
          },
          error: (error) => {
            console.error('Error loading archived courses:', error);
          }
        });
    } else {
      // If data is already loaded, just apply the filter to existing data
      this.allArchivedCourses = this.allCoursesFromServer.filter((course: Course) => {
        if (course.end_date) {
          // Parse the end date and compare with current date at the start of today
          const endDate = new Date(course.end_date);
          const currentDate = new Date();

          // Set current date to start of day to ensure we're comparing properly
          currentDate.setHours(0, 0, 0, 0);

          // Only include courses where the end date is BEFORE today (not including today)
          // This means courses ending TODAY remain visible in the main program until tomorrow
          return endDate < currentDate;
        }
        return false; // Exclude courses without end date
      });

      // Set the filtered list to all archived courses
      this.filteredArchivedCourses = [...this.allArchivedCourses];
      this._archiveCoursesSubject.next(this.filteredArchivedCourses);
    }
  }

  // Get archived courses for a specific year - use cached data
  public getArchivedCoursesByYear(year: string): void {
    if (!this.isDataLoaded) {
      // If data hasn't been loaded yet, load it first
      this.loadAllArchivedCourses();
      return;
    }

    // Use cached data to filter by year
    if (!year) {
      // If no year specified, return all archived courses
      this.filteredArchivedCourses = [...this.allArchivedCourses];
    } else {
      // Filter by the specified year
      this.filteredArchivedCourses = this.allArchivedCourses.filter((course: Course) => {
        if (course.end_date) {
          const courseYear = new Date(course.end_date).getFullYear();
          return courseYear === parseInt(year, 10);
        }
        return false;
      });
    }

    this._archiveCoursesSubject.next(this.filteredArchivedCourses);
  }

  // Get available years for archived courses
  public getAvailableYears(): string[] {
    const years = this.allArchivedCourses
      .filter((course: Course) => course.end_date)
      .map((course: Course) => new Date(course.end_date as Date).getFullYear().toString());

    // Return unique sorted years
    return Array.from(new Set(years)).sort().reverse();
  }

  // Filter archived courses by category - use cached data
  public filterByCategory(categoryId: number | string): void {
    if (!this.isDataLoaded) {
      // If data hasn't been loaded yet, load it first
      this.loadAllArchivedCourses();
      return;
    }

    if (categoryId === 'all' || categoryId === 'all') {
      this.filteredArchivedCourses = [...this.allArchivedCourses];
    } else {
      this.filteredArchivedCourses = this.allArchivedCourses.filter((course: Course) =>
        course.category_id === Number(categoryId)
      );
    }

    this._archiveCoursesSubject.next(this.filteredArchivedCourses);
  }

  // Sort archived courses by a specific field
  public sortBy(field: keyof Course, direction: 'asc' | 'desc' = 'desc'): void {
    this.filteredArchivedCourses.sort((a, b) => {
      const valueA = a[field];
      const valueB = b[field];

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return direction === 'asc' ? -1 : 1;
      if (valueB == null) return direction === 'asc' ? 1 : -1;

      let comparison = 0;
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        comparison = valueA.toLowerCase().localeCompare(valueB.toLowerCase());
      } else if (valueA instanceof Date && valueB instanceof Date) {
        comparison = valueA.getTime() - valueB.getTime();
      } else {
        comparison = (valueA as any) - (valueB as any);
      }

      return direction === 'asc' ? comparison : -comparison;
    });

    this._archiveCoursesSubject.next(this.filteredArchivedCourses);
  }

  // Search archived courses by title or content - use cached data
  public searchArchivedCourses(searchTerm: string): void {
    if (!this.isDataLoaded) {
      // If data hasn't been loaded yet, load it first
      this.loadAllArchivedCourses();
      return;
    }

    if (!searchTerm) {
      // If no search term, reset to the current archived courses
      this.filteredArchivedCourses = [...this.allArchivedCourses];
      this._archiveCoursesSubject.next(this.filteredArchivedCourses);
      return;
    }

    const filtered = this.allArchivedCourses.filter((course: Course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    this.filteredArchivedCourses = filtered;
    this._archiveCoursesSubject.next(filtered);
  }

  // Get total count of archived courses
  public getTotalCount(): number {
    return this.allArchivedCourses.length;
  }

  // Get count of currently filtered archived courses
  public getFilteredCount(): number {
    return this.filteredArchivedCourses.length;
  }

  // Reset filters to show all archived courses
  public resetFilters(): void {
    this.filteredArchivedCourses = [...this.allArchivedCourses];
    this._archiveCoursesSubject.next(this.filteredArchivedCourses);
  }

  // Force reload data from server (useful when date changes)
  public reloadData(): void {
    this.isDataLoaded = false;
    this.loadAllArchivedCourses();
  }
}
